import { sidesFor } from 'react/matchHistoryPopup/setSides';
import { SLIPPI_ID_TYPE } from 'components/connectCode';
import { slippiCharacter } from 'slippi/friendlies/gameCatalogs';

// Pure helpers for the match history popup.

export const PAGE_SIZE = 10;
export const SHOWN_SETS = 8;
const FORM_LENGTH = 10;

// 'win' / 'loss' for the newest sets that have a winner, newest first.
export function recentForm(matches, player) {
	return matches
		.filter((match) => match.hasWinner())
		.slice(0, FORM_LENGTH)
		.map((match) => (sidesFor(match, player).won ? 'win' : 'loss'));
}

// League, rank and record for one game; null for All games.
export function gameStanding(summary, ladderId) {
	if(!summary || !ladderId) {
		return null;
	}
	return summary.games.find((game) => game.ladder_id === ladderId) || null;
}

// Ranked score against the other player, in one game or in total.
export function headToHeadRecord(summary, ladderId) {
	const headToHead = summary && summary.head_to_head;
	if(!headToHead) {
		return null;
	}
	if(!ladderId) {
		return { wins: headToHead.wins, losses: headToHead.losses };
	}
	const ladder = headToHead.ladders.find((entry) => entry.ladder_id === ladderId);
	return ladder ? { wins: ladder.wins, losses: ladder.losses } : { wins: 0, losses: 0 };
}

const siteCharacter = (character) => ({ key: `character-${character.id}`, name: character.name, image: character.image_url });

// The port of each replay game that was the player's: theirs by player id,
// or, for an opponent the replay couldn't name, the other port of a game the
// viewer's player was in.
function replayPorts(match, player) {
	return (match.replay_games || []).map((game) => {
		const named = game.players.find((port) => port.player_id === player.id);
		if(named || game.players.length !== 2) {
			return named || null;
		}
		const other = game.players.find((port) => port.player_id !== null && port.player_id !== player.id);
		return other ? game.players.find((port) => port !== other) : null;
	}).filter(Boolean);
}

// Melee replays' character ids are Slippi's. Brawl Online ladders' ids need
// the season's characters, which the popup doesn't load, so they fall back to mains.
function replayCharacters(match, player) {
	if(!match.ladder || match.ladder.player_code_type !== SLIPPI_ID_TYPE) {
		return [];
	}
	const characters = [];
	replayPorts(match, player).forEach((port) => {
		const character = slippiCharacter(port.character_id);
		if(character && !characters.some((existing) => existing.name === character.name)) {
			characters.push({ key: `replay-${port.character_id}`, name: character.name, image: character.portrait });
		}
	});
	return characters;
}

// The characters to show for one side of a set: those reported for it, those
// in its linked replays, or the player's mains as a guess (most friendlies).
// source is 'played', 'mains', or null when there is nothing to show.
export function setSide(match, player) {
	const reported = (match.set_characters && match.set_characters[player.id]) || [];
	if(reported.length) {
		return { source: 'played', characters: reported.map(siteCharacter) };
	}
	const fromReplays = replayCharacters(match, player);
	if(fromReplays.length) {
		return { source: 'played', characters: fromReplays };
	}
	const mains = (match.set_mains && match.set_mains[player.id]) || [];
	return mains.length ? { source: 'mains', characters: mains.map(siteCharacter) } : { source: null, characters: [] };
}

// The player's record in the set's replay games, oldest first; null when
// there are none with the player in them.
export function replayResult(match, player) {
	const games = (match.replay_games || []).map((game) => game.players.find((port) => port.player_id === player.id)).filter(Boolean).map((port) => !!port.won);
	if(!games.length) {
		return null;
	}
	const wins = games.filter(Boolean).length;
	return { wins, losses: games.length - wins, games };
}
